import { useState, useEffect } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

// Flattens the nested category tree into a single list with an indent
// level, so it can be shown in a plain dropdown while still visually
// conveying the hierarchy (e.g. "— Beer" under "Grocery").
const flattenTree = (nodes, depth = 0, excludeId = null, out = []) => {
    nodes.forEach((node) => {
        if (node._id === excludeId) return; // can't parent under yourself
        out.push({ _id: node._id, name: node.name, depth });
        if (node.children && node.children.length > 0) {
            flattenTree(node.children, depth + 1, excludeId, out);
        }
    });
    return out;
};

const CategoryForm = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const isEditMode = Boolean(id);

    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [name, setName] = useState("");
    const [nameZh, setNameZh] = useState("");
    const [parent, setParent] = useState(searchParams.get('parent') || "");
    const [parentOptions, setParentOptions] = useState([]);
    const [requiresAgeVerification, setRequiresAgeVerification] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data } = await axios.get('/api/v1/categories');
                setParentOptions(flattenTree(data.categories, 0, isEditMode ? id : null));

                if (isEditMode) {
                    // Find the category being edited within the tree (it may be nested)
                    const findInTree = (nodes) => {
                        for (const node of nodes) {
                            if (node._id === id) return node;
                            if (node.children) {
                                const found = findInTree(node.children);
                                if (found) return found;
                            }
                        }
                        return null;
                    };
                    const found = findInTree(data.categories);
                    if (found) {
                        setName(found.name);
                        setNameZh(found.nameZh || "");
                        setParent(found.parent || "");
                        setRequiresAgeVerification(!!found.requiresAgeVerification);
                    } else {
                        enqueueSnackbar("Category not found", { variant: "error" });
                        navigate('/admin/categories');
                    }
                }
            } catch (error) {
                enqueueSnackbar(error?.response?.data?.message || "Failed to load categories", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const submitHandler = async (e) => {
        e.preventDefault();

        if (!name.trim()) return enqueueSnackbar("Please enter a category name", { variant: "warning" });

        const payload = { name, nameZh, parent: parent || null, requiresAgeVerification };

        setSaving(true);
        try {
            if (isEditMode) {
                await axios.put(`/api/v1/admin/category/${id}`, payload);
                enqueueSnackbar("Category updated", { variant: "success" });
            } else {
                await axios.post('/api/v1/admin/category/new', payload);
                enqueueSnackbar("Category created", { variant: "success" });
            }
            navigate('/admin/categories');
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save category", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title={`Admin: ${isEditMode ? "Edit" : "New"} Category | 1313 Mart`} />
            {saving && <BackdropLoader />}

            <form onSubmit={submitHandler} className="flex flex-col gap-4 bg-white rounded-lg shadow p-4 sm:p-6 max-w-lg" id="categoryform">

                <h1 className="text-lg font-medium uppercase">{isEditMode ? "Edit" : "New"} Category</h1>

                <TextField
                    label="Category Name (English)"
                    variant="outlined"
                    size="small"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    helperText={!isEditMode ? "The URL slug is generated from this automatically" : undefined}
                />

                <TextField
                    label="Category Name (中文 — optional)"
                    variant="outlined"
                    size="small"
                    value={nameZh}
                    onChange={(e) => setNameZh(e.target.value)}
                    helperText="Shown on the website when a visitor switches to Chinese. Leave blank to keep showing the English name."
                />

                <TextField
                    label="Parent Category (optional)"
                    select
                    variant="outlined"
                    size="small"
                    value={parent}
                    onChange={(e) => setParent(e.target.value)}
                    helperText="Leave blank to make this a top-level category"
                >
                    <MenuItem value="">None — top-level category</MenuItem>
                    {parentOptions.map((opt) => (
                        <MenuItem value={opt._id} key={opt._id}>
                            {"— ".repeat(opt.depth)}{opt.name}
                        </MenuItem>
                    ))}
                </TextField>

                <FormControlLabel
                    control={<Checkbox checked={requiresAgeVerification} onChange={(e) => setRequiresAgeVerification(e.target.checked)} />}
                    label="Requires Age Verification (18+) — e.g. Alcohol"
                />
                <p className="text-xs text-gray-500 -mt-2">Enabling this automatically applies to every subcategory underneath it too — no need to flag each one individually.</p>

                <div className="flex justify-end">
                    <input form="categoryform" type="submit" className="bg-primary-orange uppercase px-6 py-3 text-white font-medium rounded shadow hover:shadow-lg cursor-pointer" value={isEditMode ? "Save Changes" : "Create Category"} />
                </div>

            </form>
        </>
    );
};

export default CategoryForm;